/**
 * @file pages/Hotels.jsx
 * @description Premium hotel search page with real-time Amadeus API.
 * Live city autocomplete, rich hotel cards, detail modal, booking flow.
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Hotel, Search as SearchIcon, MapPin, Star, Wifi, Car, Dumbbell,
  UtensilsCrossed, Waves, Coffee, ChevronRight,
  Calendar, Users, X, Check, ArrowRight, Heart,
  SlidersHorizontal, Loader2, AlertCircle, BedDouble,
  Shield, Sparkles, Sun, Moon, CreditCard, Ban,
  Building2, DoorOpen, Wind, ConciergeBell, Globe
} from 'lucide-react';
import Header from '../Components/Layouts/Header';
import Footer from '../Components/Layouts/Footer';
import AnimatedButton from '../Components/Ui/AnimatedButton';
import { searchHotelCities, getPopularHotels, searchHotels, bookHotel } from '../services/api';
import { toast } from 'react-toastify';

// ─── AMENITY ICONS ───────────────────────────────────────────────
const AMENITY_ICONS = {
  'WiFi': Wifi, 'WIFI': Wifi, 'Pool': Waves, 'SWIMMING_POOL': Waves,
  'Spa': Sparkles, 'SPA': Sparkles, 'Gym': Dumbbell, 'FITNESS_CENTER': Dumbbell,
  'Restaurant': UtensilsCrossed, 'RESTAURANT': UtensilsCrossed, 'Bar': Coffee, 'BAR': Coffee,
  'Parking': Car, 'PARKING': Car, 'Room Service': ConciergeBell, 'ROOM_SERVICE': ConciergeBell,
  'Laundry': Wind, 'LAUNDRY_SERVICE': Wind, 'Airport Shuttle': Car, 'AIRPORT_SHUTTLE': Car,
  'Business Center': Building2, 'BUSINESS_CENTER': Building2, 'Concierge': ConciergeBell,
  'AIR_CONDITIONING': Wind, 'MINIBAR': Coffee,
};

function formatAmenity(raw) {
  return raw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\bWifi\b/i, 'WiFi');
}

// ─── HOTEL VISUAL HELPERS ────────────────────────────────────────
// Curated free Unsplash hotel/resort images (no API key needed)
const HOTEL_IMAGES = [
  'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1618773928121-c32242e63f39?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1590073242678-70ee3fc28e8e?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1455587734955-081b22074882?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1549294413-26f195200c16?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1445019980597-93fa8acb246c?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1568084680786-a84f91d1153c?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1573052905904-34ad3d3b60a1?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1522798514-97ceb8c4f1c8?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1596178065887-1198b6148b2b?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1559599238-308793637427?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1540541338287-41700207dee6?w=600&h=400&fit=crop&q=80',
];

function hashStr(s = '') {
  return s.split('').reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0);
}

function getHotelImage(hotel, index = 0) {
  if (hotel.media?.[0]?.uri) return hotel.media[0].uri;
  const h = Math.abs(hashStr(hotel.hotelId || hotel.name || '') + index);
  return HOTEL_IMAGES[h % HOTEL_IMAGES.length];
}

const CARD_GRADIENTS = [
  'from-[#1a2463] to-[#3d4fc2]', 'from-[#1e3a5f] to-[#4a90d9]',
  'from-[#2d1b69] to-[#7c3aed]', 'from-[#134e4a] to-[#14b8a6]',
  'from-[#7c2d12] to-[#ea580c]', 'from-[#581c87] to-[#a855f7]',
];

function getHotelGradient(hotel, index = 0) {
  const h = Math.abs(hashStr(hotel.hotelId || hotel.name || '') + index);
  return CARD_GRADIENTS[h % CARD_GRADIENTS.length];
}

// Hotel image component: shows image with gradient loading fallback
const HotelImage = ({ hotel, index = 0, className = '' }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const imgSrc = getHotelImage(hotel, index);
  const gradient = getHotelGradient(hotel, index);

  return (
    <div className={`relative w-full h-full overflow-hidden ${className}`}>
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient} transition-opacity duration-500 ${loaded ? 'opacity-0' : 'opacity-100'}`} />
      {!failed && (
        <img src={imgSrc} alt={hotel.name}
          className={`w-full h-full object-cover transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'} group-hover:scale-110 transition-transform duration-700`}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)} />
      )}
    </div>
  );
};

// ─── DEBOUNCE HOOK ───────────────────────────────────────────────
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── POPULAR DESTINATIONS ────────────────────────────────────────
const POPULAR_DESTINATIONS = [
  { name: 'New Delhi', code: 'DEL', country: 'India', img: 'https://images.unsplash.com/photo-1587474260584-136574528ed5?w=400&q=80' },
  { name: 'Mumbai', code: 'BOM', country: 'India', img: 'https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=400&q=80' },
  { name: 'Goa', code: 'GOI', country: 'India', img: 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=400&q=80' },
  { name: 'Jaipur', code: 'JAI', country: 'India', img: 'https://images.unsplash.com/photo-1477587458883-47145ed94245?w=400&q=80' },
  { name: 'Dubai', code: 'DXB', country: 'UAE', img: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=400&q=80' },
  { name: 'Singapore', code: 'SIN', country: 'Singapore', img: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=400&q=80' },
  { name: 'Bangkok', code: 'BKK', country: 'Thailand', img: 'https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=400&q=80' },
  { name: 'London', code: 'LON', country: 'UK', img: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=400&q=80' },
];

// ─── STAR RENDERER ───────────────────────────────────────────────
const Stars = ({ count }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={12} className={i < count ? 'text-amber-400 fill-amber-400' : 'text-gray-200'} />
    ))}
  </div>
);

// ─── CITY AUTOCOMPLETE ───────────────────────────────────────────
const CityAutocomplete = ({ value, onChange, onSelectCity }) => {
  const [input, setInput] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const debouncedInput = useDebounce(input, 300);

  useEffect(() => { if (value !== undefined && value !== input) setInput(value); }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (!wrapperRef.current?.contains(e.target) && !dropdownRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!debouncedInput || debouncedInput.length < 2) { setSuggestions([]); return; }
    setLoading(true);
    searchHotelCities(debouncedInput)
      .then(res => {
        const cities = res?.data?.data?.cities || [];
        setSuggestions(cities.slice(0, 8));
      })
      .catch(() => setSuggestions([]))
      .finally(() => setLoading(false));
  }, [debouncedInput]);

  const handleSelect = (city) => {
    const display = `${city.cityName || city.name} (${city.code})`;
    setInput(display);
    onChange?.(display);
    onSelectCity?.(city);
    setOpen(false);
  };

  const getPos = () => {
    if (!wrapperRef.current) return {};
    const r = wrapperRef.current.getBoundingClientRect();
    return { position: 'fixed', top: r.bottom + 4, left: r.left, width: Math.max(r.width, 320), zIndex: 9999 };
  };

  const dropdown = open && (suggestions.length > 0 || loading) && createPortal(
    <div ref={dropdownRef} style={getPos()} className="bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
      {loading && suggestions.length === 0 && (
        <div className="px-4 py-6 flex items-center justify-center gap-2 text-gray-400">
          <Loader2 size={16} className="animate-spin" /> Searching cities...
        </div>
      )}
      {suggestions.map((city, i) => (
        <button key={`${city.code}-${i}`} type="button" onMouseDown={e => e.preventDefault()} onClick={() => handleSelect(city)}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-indigo-50 transition-colors text-left border-b border-gray-50 last:border-0 group">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-500 group-hover:bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <MapPin size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-gray-900 truncate">{city.cityName || city.name}</span>
              <span className="text-[11px] font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded flex-shrink-0">{city.code}</span>
            </div>
            <p className="text-[11px] text-gray-400 mt-0.5">{city.countryName || city.countryCode}</p>
          </div>
          <Globe size={14} className="text-gray-200 group-hover:text-indigo-400 flex-shrink-0" />
        </button>
      ))}
    </div>,
    document.body
  );

  return (
    <div ref={wrapperRef} className="relative">
      <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">
        <MapPin size={11} className="inline mr-1" />City / Destination
      </label>
      <div className="flex items-center gap-2">
        <input type="text" value={input}
          onChange={e => { setInput(e.target.value); onChange?.(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Where are you going?"
          className="w-full text-lg font-bold text-gray-900 bg-transparent outline-none placeholder:text-gray-300 placeholder:font-normal"
          autoComplete="off" required />
        {input && (
          <button type="button" onClick={() => { setInput(''); onChange?.(''); }} className="text-gray-300 hover:text-gray-500 p-0.5"><X size={16} /></button>
        )}
        {loading && <Loader2 size={16} className="text-indigo-400 animate-spin flex-shrink-0" />}
      </div>
      {dropdown}
    </div>
  );
};

// ─── SEARCH FORM ─────────────────────────────────────────────────
const HotelSearchForm = ({ onSearch, searching, initialValues }) => {
  const [city, setCity] = useState(initialValues?.city || '');
  const [selectedCity, setSelectedCity] = useState(null);
  const [checkIn, setCheckIn] = useState(initialValues?.checkIn || '');
  const [checkOut, setCheckOut] = useState(initialValues?.checkOut || '');
  const [adults, setAdults] = useState(initialValues?.adults || 2);
  const [rooms, setRooms] = useState(initialValues?.rooms || 1);
  const [showGuests, setShowGuests] = useState(false);
  const guestRef = useRef(null);

  const today = new Date().toISOString().split('T')[0];
  const minCheckOut = checkIn || today;

  useEffect(() => {
    const handler = (e) => { if (!guestRef.current?.contains(e.target)) setShowGuests(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!city.trim() || !checkIn || !checkOut) return;
    if (new Date(checkOut) <= new Date(checkIn)) { toast.error('Check-out must be after check-in'); return; }
    onSearch({ city: city.trim(), checkIn, checkOut, adults, rooms });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-0 border border-gray-200 rounded-2xl bg-white overflow-visible">
        {/* CITY — 5 cols */}
        <div className="lg:col-span-5 p-4 md:p-5 border-b md:border-b-0 md:border-r border-gray-200 hover:bg-blue-50/30 transition-colors">
          <CityAutocomplete value={city} onChange={setCity} onSelectCity={setSelectedCity} />
        </div>

        {/* CHECK-IN — 2 cols */}
        <div className="lg:col-span-2 p-4 md:p-5 border-b md:border-b-0 md:border-r border-gray-200 hover:bg-blue-50/30 transition-colors cursor-pointer"
          onClick={() => document.getElementById('hotel-checkin')?.showPicker?.()}>
          <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">
            <Calendar size={11} className="inline mr-1" />Check-in
          </label>
          <input id="hotel-checkin" type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)}
            min={today} className="w-full text-base font-bold text-gray-900 bg-transparent outline-none cursor-pointer" required />
        </div>

        {/* CHECK-OUT — 2 cols */}
        <div className="lg:col-span-2 p-4 md:p-5 border-b md:border-b-0 md:border-r border-gray-200 hover:bg-blue-50/30 transition-colors cursor-pointer"
          onClick={() => document.getElementById('hotel-checkout')?.showPicker?.()}>
          <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">
            <Calendar size={11} className="inline mr-1" />Check-out
          </label>
          <input id="hotel-checkout" type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)}
            min={minCheckOut} className="w-full text-base font-bold text-gray-900 bg-transparent outline-none cursor-pointer" required />
        </div>

        {/* GUESTS & ROOMS — 3 cols, dropdown */}
        <div ref={guestRef} className="lg:col-span-3 p-4 md:p-5 relative hover:bg-blue-50/30 transition-colors">
          <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">
            <Users size={11} className="inline mr-1" />Guests & Rooms
          </label>
          <button type="button" onClick={() => setShowGuests(!showGuests)}
            className="w-full text-left text-base font-bold text-gray-900 mt-0.5 focus:outline-none">
            {adults} Adult{adults > 1 ? 's' : ''}, {rooms} Room{rooms > 1 ? 's' : ''}
          </button>

          <AnimatePresence>
            {showGuests && (
              <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
                className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 p-5 z-50 w-64">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div><p className="text-sm font-bold text-gray-900">Adults</p><p className="text-[11px] text-gray-400">Ages 18+</p></div>
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => setAdults(Math.max(1, adults - 1))}
                        className="w-8 h-8 rounded-full border border-gray-200 text-gray-500 hover:border-[#243185] hover:text-[#243185] flex items-center justify-center text-lg font-semibold transition-colors">−</button>
                      <span className="text-base font-bold text-gray-900 w-5 text-center">{adults}</span>
                      <button type="button" onClick={() => setAdults(Math.min(9, adults + 1))}
                        className="w-8 h-8 rounded-full border border-gray-200 text-gray-500 hover:border-[#243185] hover:text-[#243185] flex items-center justify-center text-lg font-semibold transition-colors">+</button>
                    </div>
                  </div>
                  <div className="border-t border-gray-100" />
                  <div className="flex items-center justify-between">
                    <div><p className="text-sm font-bold text-gray-900">Rooms</p><p className="text-[11px] text-gray-400">Max 5</p></div>
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => setRooms(Math.max(1, rooms - 1))}
                        className="w-8 h-8 rounded-full border border-gray-200 text-gray-500 hover:border-[#243185] hover:text-[#243185] flex items-center justify-center text-lg font-semibold transition-colors">−</button>
                      <span className="text-base font-bold text-gray-900 w-5 text-center">{rooms}</span>
                      <button type="button" onClick={() => setRooms(Math.min(5, rooms + 1))}
                        className="w-8 h-8 rounded-full border border-gray-200 text-gray-500 hover:border-[#243185] hover:text-[#243185] flex items-center justify-center text-lg font-semibold transition-colors">+</button>
                    </div>
                  </div>
                  <button type="button" onClick={() => setShowGuests(false)}
                    className="w-full mt-2 bg-[#243185] text-white text-sm font-bold rounded-lg py-2 hover:bg-[#1a2463] transition-colors">
                    Done
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="flex justify-center mt-6">
        <AnimatedButton type="submit" loading={searching} disabled={searching} variant="gradient" size="xl" icon={SearchIcon}>
          Search Hotels
        </AnimatedButton>
      </div>
    </form>
  );
};

// ─── FILTER SIDEBAR ──────────────────────────────────────────────
const FilterPanel = ({ filters, onChange, resultCount }) => (
  <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 lg:sticky lg:top-24 space-y-5 sm:space-y-6 border border-gray-100">
    <div className="flex items-center justify-between">
      <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
        <SlidersHorizontal size={14} className="text-[#243185]" /> Filters
      </h3>
      <span className="text-[10px] bg-[#243185] text-white px-2.5 py-1 rounded-full font-bold">{resultCount}</span>
    </div>

    {/* Price Range */}
    <div>
      <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">Budget per night</label>
      <input type="range" min={0} max={50000} step={1000} value={filters.maxPrice}
        onChange={(e) => onChange({ ...filters, maxPrice: Number(e.target.value) })}
        className="w-full mt-3 accent-[#243185] h-1.5" />
      <div className="flex justify-between text-[10px] text-gray-400 mt-1">
        <span>₹0</span>
        <span className="font-bold text-[#243185] text-xs">₹{filters.maxPrice?.toLocaleString()}</span>
      </div>
    </div>

    {/* Star Rating */}
    <div>
      <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">Star Rating</label>
      <div className="flex gap-2 mt-2">
        {[3, 4, 5].map(s => (
          <button key={s} type="button"
            onClick={() => onChange({ ...filters, minStars: filters.minStars === s ? 0 : s })}
            className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold border transition-all ${
              filters.minStars === s
                ? 'border-[#243185] bg-[#243185] text-white shadow-md'
                : 'border-gray-200 text-gray-500 hover:border-gray-300 bg-white'
            }`}>
            {s} <Star size={10} className={filters.minStars === s ? 'fill-white' : 'fill-current'} />+
          </button>
        ))}
      </div>
    </div>

    {/* Sort */}
    <div>
      <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">Sort By</label>
      <select value={filters.sortBy} onChange={(e) => onChange({ ...filters, sortBy: e.target.value })}
        className="w-full mt-2 text-sm border border-gray-200 rounded-lg px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#243185]/20 bg-white font-semibold text-gray-700">
        <option value="price_low">Price: Low → High</option>
        <option value="price_high">Price: High → Low</option>
        <option value="stars">Stars: Best First</option>
      </select>
    </div>
  </div>
);

// ─── HOTEL CARD ──────────────────────────────────────────────────
const HotelCard = ({ hotel, onSelect, index }) => {
  const perNight = hotel.price?.perNight || Math.round(hotel.price?.total / (hotel.nights || 1));
  const amenities = (hotel.amenities || []).slice(0, 6);
  const currency = hotel.price?.currency === 'EUR' ? '€' : hotel.price?.currency === 'USD' ? '$' : '₹';

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      className="bg-white rounded-2xl border border-gray-100 hover:shadow-2xl hover:border-[#243185]/20 transition-all duration-300 overflow-hidden group cursor-pointer"
      onClick={() => onSelect(hotel)}
    >
      <div className="flex flex-col sm:flex-row">
        {/* IMAGE */}
        <div className="sm:w-48 md:w-80 h-48 sm:h-auto relative flex-shrink-0 overflow-hidden">
          <HotelImage hotel={hotel} index={index} size="md" />
          {hotel.rating && (
            <div className="absolute top-3 left-3 flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded-lg px-2.5 py-1.5 shadow-sm z-10">
              <Star size={12} className="text-amber-400 fill-amber-400" />
              <span className="text-xs font-bold text-gray-900">{hotel.rating}-star</span>
            </div>
          )}
        </div>

        {/* DETAILS */}
        <div className="flex-1 p-4 sm:p-5 md:p-6 flex flex-col">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-black text-gray-900 group-hover:text-[#243185] transition-colors leading-tight">
                {hotel.name}
              </h3>
              <p className="text-xs text-gray-500 flex items-center gap-1 mt-1.5">
                <MapPin size={12} className="text-[#243185]" />
                {hotel.address?.cityName || hotel.cityCode}
                {hotel.address?.countryCode ? `, ${hotel.address.countryCode}` : ''}
              </p>
            </div>
          </div>

          {/* AMENITIES */}
          {amenities.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-4">
              {amenities.map((a, i) => {
                const Icon = AMENITY_ICONS[a] || Check;
                return (
                  <span key={i} className="inline-flex items-center gap-1 text-[10px] text-gray-600 bg-gray-50 px-2.5 py-1.5 rounded-lg font-medium">
                    <Icon size={11} className="text-[#243185]" /> {formatAmenity(a)}
                  </span>
                );
              })}
              {hotel.amenities?.length > 6 && (
                <span className="text-[10px] text-[#243185] font-bold px-2 py-1.5">+{hotel.amenities.length - 6} more</span>
              )}
            </div>
          )}

          {/* ROOM INFO (only for offers) */}
          {hotel.room?.type && (
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
              <BedDouble size={13} className="text-gray-400" />
              <span className="font-medium">{hotel.room.type}</span>
              {hotel.room.bedType && (
                <>
                  <span className="text-gray-300">·</span>
                  <span>{hotel.room.bedType === 'KING' ? 'King Bed' : hotel.room.bedType === 'TWIN' ? 'Twin Beds' : hotel.room.bedType.replace(/_/g, ' ')}</span>
                </>
              )}
            </div>                                                                                                      
          )}

          {/* Address lines (for listings without pricing) */}
          {!hotel.price?.total && hotel.address?.lines?.[0] && (
            <p className="mt-3 text-xs text-gray-500 truncate">{hotel.address.lines[0]}</p>
          )}

          {/* Distance from center (for listings) */}
          {hotel.distance && (
            <p className="mt-1 text-[11px] text-gray-400">{hotel.distance.value} {hotel.distance.unit} from city center</p>
          )}

          {/* PRICE + CTA */}
          <div className="mt-auto pt-5 flex items-end justify-between border-t border-gray-100 mt-5">
            {hotel.price?.total ? (
              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
                  {hotel.nights || 1} night{(hotel.nights || 1) > 1 ? 's' : ''}
                </p>
                <div className="flex items-baseline gap-1.5 mt-1">
                  <span className="text-2xl font-black text-gray-900">{currency}{perNight?.toLocaleString()}</span>
                  <span className="text-xs text-gray-400 font-medium">/ night</span>
                </div>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  Total: <span className="font-bold text-gray-700">{currency}{hotel.price.total.toLocaleString()}</span> incl. taxes
                </p>
              </div>
            ) : (
              <div>
                <p className="text-xs text-gray-500 font-semibold">Real hotel from Amadeus</p>
                <p className="text-[10px] text-gray-400 mt-0.5">Contact us for best rates</p>
              </div>
            )}
            <AnimatedButton variant="gradient" size="sm" icon={ArrowRight} iconPosition="right"
              onClick={(e) => { e.stopPropagation(); onSelect(hotel); }}>
              {hotel.price?.total ? 'View Deal' : 'View Details'}
            </AnimatedButton>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ─── HOTEL DETAIL MODAL ──────────────────────────────────────────
const HotelDetailModal = ({ hotel, onClose, onBook }) => {
  const [booking, setBooking] = useState(false);
  const perNight = hotel.price?.perNight || Math.round(hotel.price?.total / (hotel.nights || 1));
  const currency = hotel.price?.currency === 'EUR' ? '€' : hotel.price?.currency === 'USD' ? '$' : '₹';
  const amenities = hotel.amenities || [];

  const handleBook = async () => { setBooking(true); await onBook(hotel); setBooking(false); };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* HERO */}
        <div className="relative h-48 md:h-56 overflow-hidden rounded-t-2xl">
          <HotelImage hotel={hotel} size="lg" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent z-[1]" />
          <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white flex items-center justify-center shadow-lg z-10"><X size={18} /></button>
          <div className="absolute bottom-0 left-0 right-0 p-6 z-[2]">
            <div className="flex items-center gap-2 mb-2">
              {hotel.chainCode && <span className="text-[10px] bg-white/20 backdrop-blur-sm text-white font-bold px-2 py-1 rounded-md tracking-wider">{hotel.chainCode}</span>}
              <Hotel size={16} className="text-white/60" />
            </div>
            <h2 className="text-2xl font-black text-white drop-shadow-lg">{hotel.name}</h2>
            <div className="flex items-center gap-3 mt-1.5">
              {hotel.rating && <div className="flex gap-0.5">{Array.from({ length: hotel.rating }, (_, i) => <Star key={i} size={14} className="text-amber-400 fill-amber-400" />)}</div>}
              <span className="text-xs text-white/80 flex items-center gap-1"><MapPin size={11} /> {hotel.address?.cityName || hotel.cityCode}</span>
            </div>
          </div>
        </div>

        <div className="px-6 py-6 space-y-6">
          {/* Quick Info Grid — only show date/room info for offers */}
          {hotel.price?.total ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { icon: Calendar, label: 'Check-in', value: hotel.checkIn ? new Date(hotel.checkIn).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '-' },
                { icon: Calendar, label: 'Check-out', value: hotel.checkOut ? new Date(hotel.checkOut).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '-' },
                { icon: Moon, label: 'Duration', value: `${hotel.nights || 1} Night${(hotel.nights || 1) > 1 ? 's' : ''}` },
                { icon: BedDouble, label: 'Room Type', value: hotel.room?.type || 'Standard' },
              ].map((item, i) => (
                <div key={i} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 text-center border border-gray-100">
                  <item.icon size={18} className="text-[#243185] mx-auto mb-1.5" />
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">{item.label}</p>
                  <p className="text-sm font-bold text-gray-900 mt-0.5">{item.value}</p>
                </div>
              ))}
            </div>
          ) : (
            /* Listing-only info grid */
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { icon: MapPin, label: 'City', value: hotel.address?.cityName || hotel.cityCode || '-' },
                { icon: Globe, label: 'Country', value: hotel.address?.countryCode || '-' },
                hotel.distance ? { icon: Building2, label: 'Distance', value: `${hotel.distance.value} ${hotel.distance.unit} from center` } : null,
              ].filter(Boolean).map((item, i) => (
                <div key={i} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 text-center border border-gray-100">
                  <item.icon size={18} className="text-[#243185] mx-auto mb-1.5" />
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">{item.label}</p>
                  <p className="text-sm font-bold text-gray-900 mt-0.5">{item.value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Address */}
          {hotel.address?.lines?.[0] && (
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 flex items-start gap-3">
              <MapPin size={16} className="text-[#243185] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-gray-900">Address</p>
                <p className="text-xs text-gray-600 mt-0.5">{hotel.address.lines.join(', ')}{hotel.address?.cityName ? `, ${hotel.address.cityName}` : ''}</p>
              </div>
            </div>
          )}

          {/* Room Description */}
          {hotel.room?.description && (
            <div className="bg-blue-50/50 rounded-xl p-5 border border-blue-100">
              <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2"><BedDouble size={14} className="text-[#243185]" /> Room Details</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{hotel.room.description}</p>
            </div>
          )}

          {/* Amenities */}
          {amenities.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3">Amenities & Facilities</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {amenities.map((a, i) => {
                  const Icon = AMENITY_ICONS[a] || Check;
                  return (
                    <div key={i} className="flex items-center gap-2.5 text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2.5 border border-gray-100">
                      <Icon size={15} className="text-[#243185] flex-shrink-0" /> {formatAmenity(a)}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Cancellation Policy */}
          {hotel.policies?.cancellation && (
            <div className="flex items-start gap-3 bg-emerald-50 rounded-xl p-4 border border-emerald-100">
              <Shield size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-emerald-800">Cancellation Policy</p>
                <p className="text-xs text-emerald-600 mt-0.5">{hotel.policies.cancellation.description || 'Contact us for cancellation details'}</p>
              </div>
            </div>
          )}
          
          {/* PRICE + BOOK (only for offers with pricing) */}
          {hotel.price?.total ? (
            <div className="bg-gradient-to-r from-[#1a2463] to-[#3d4fc2] rounded-2xl p-6 text-white">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <p className="text-[11px] text-white/60 uppercase tracking-wider font-semibold">Total for {hotel.nights || 1} night{(hotel.nights || 1) > 1 ? 's' : ''}</p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-3xl font-black">{currency}{hotel.price.total.toLocaleString()}</span>
                    <span className="text-sm text-white/60">incl. taxes</span>
                  </div>
                  <p className="text-xs text-white/50 mt-1">{currency}{perNight?.toLocaleString()} per night</p>
                </div>
                <AnimatedButton variant="outline" size="xl" icon={CreditCard} loading={booking} disabled={booking} onClick={handleBook}
                  className="!bg-white !text-[#243185] hover:!bg-gray-100 !border-white font-black">
                  Book Now
                </AnimatedButton>
              </div>
            </div>
          ) : (
            /* Contact CTA for listing-only hotels */
            <div className="bg-gradient-to-r from-[#1a2463] to-[#3d4fc2] rounded-2xl p-6 text-white">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <p className="text-[11px] text-white/60 uppercase tracking-wider font-semibold">Verified Amadeus Property</p>
                  <p className="text-lg font-black mt-1">Want to book this hotel?</p>
                  <p className="text-xs text-white/50 mt-1">Our team will find you the best rates</p>
                </div>
                <AnimatedButton variant="outline" size="xl" icon={ArrowRight} onClick={handleBook}
                  className="!bg-white !text-[#243185] hover:!bg-gray-100 !border-white font-black">
                  Get Best Price
                </AnimatedButton>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── MAIN PAGE ───────────────────────────────────────────────────
const Hotels = () => {
  const [searchParams, setSearchParams] = useState(null);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [filters, setFilters] = useState({ maxPrice: 50000, minStars: 0, sortBy: 'price_low' });

  // Search results query
  const { data, isLoading, error } = useQuery({
    queryKey: ['hotels', searchParams],
    queryFn: async () => {
      if (!searchParams) return null;
      const res = await searchHotels(searchParams);
      return res?.data || null;
    },
    enabled: !!searchParams,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Popular hotels for landing page (real Amadeus data)
  const { data: popularData, isLoading: popularLoading } = useQuery({
    queryKey: ['popularHotels'],                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                    
    queryFn: async () => {
      const res = await getPopularHotels(4);
      return res?.data?.data?.cities || [];
    },
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !searchParams,
  });

  const searchData = data?.data || data;
  const source = data?.source || '';

  const results = useMemo(() => {
    let items = searchData?.results || [];
    if (filters.maxPrice < 50000) {
      items = items.filter(h => {
        const pn = h.price?.perNight || Math.round((h.price?.total || 0) / (h.nights || 1));
        return !h.price?.total || pn <= filters.maxPrice;
      });
    }
    if (filters.minStars > 0) items = items.filter(h => (h.rating || 0) >= filters.minStars);
    const sorted = [...items];
    switch (filters.sortBy) {
      case 'price_low': sorted.sort((a, b) => (a.price?.total || 0) - (b.price?.total || 0)); break;
      case 'price_high': sorted.sort((a, b) => (b.price?.total || 0) - (a.price?.total || 0)); break;
      case 'stars': sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0)); break;
    }
    return sorted;
  }, [searchData, filters]);

  const handleSearch = (params) => { setSearchParams(params); setSelectedHotel(null); };

  const handleBook = async (hotel) => {
    toast.success(`Booking "${hotel.name}" — our team will contact you shortly!`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Helmet>
        <title>Search Hotels | Nikaba Travels</title>
        <meta name="description" content="Find and book the best hotels worldwide. Real-time prices from Amadeus. Powered by Nikaba Travels." />
      </Helmet>
      <Header />

      {/* ════ HERO ════ */}
      <div className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 h-[520px]">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0f1741] via-[#1a2463] to-[#2d3d9e]" />
          <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-50 to-transparent" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-28 pb-8">
          {/* Title */}
          <div className="text-center mb-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-4">
              <Sparkles size={14} className="text-amber-300" />
              <span className="text-xs font-bold text-white/80">Powered by Amadeus — Real-Time Prices</span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="text-2xl sm:text-3xl md:text-5xl font-black text-white leading-tight">
              Find Your Perfect <span className="bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent">Hotel</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="text-white/50 mt-3 text-sm max-w-md mx-auto">
              Compare thousands of hotels worldwide with live pricing from global distribution systems
            </motion.p>
          </div>

          {/* Search Widget */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="bg-white rounded-2xl shadow-2xl shadow-black/20 p-3 sm:p-5 md:p-7">
            <HotelSearchForm onSearch={handleSearch} searching={isLoading} initialValues={searchParams} />
          </motion.div>
        </div>
      </div>

      {/* ════ CONTENT ════ */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Error */}
        {error && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-start gap-3 mb-6">
            <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-red-800">Search Error</p>
              <p className="text-xs text-red-600 mt-1">{error?.message || 'Something went wrong. Please try a different city or dates.'}</p>
            </div>
          </motion.div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="bg-white rounded-2xl shadow-sm p-10 sm:p-20 flex flex-col items-center gap-5 border border-gray-100">
            <div className="relative">
              <div className="w-20 h-20 rounded-full border-4 border-gray-100 border-t-[#243185] animate-spin" />
              <Hotel size={24} className="absolute inset-0 m-auto text-[#243185]" />
            </div>
            <div className="text-center">
              <p className="text-gray-800 font-bold text-lg">Searching hotels...</p>
              <p className="text-xs text-gray-400 mt-1">Fetching real-time prices from Amadeus</p>
            </div>
          </div>
        )}

        {/* Results */}
        {!isLoading && results.length > 0 && (
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="lg:w-72 flex-shrink-0">
              <FilterPanel filters={filters} onChange={setFilters} resultCount={results.length} />
            </div>
            <div className="flex-1 space-y-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-black text-gray-900">
                  {results.length} hotel{results.length !== 1 ? 's' : ''} found
                  {searchParams?.city && <span className="text-[#243185]"> in {searchParams.city}</span>}
                </h2>
                <span className={`text-[10px] px-3 py-1.5 rounded-full font-bold border flex items-center gap-1 ${
                  source === 'amadeus_offers'
                    ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                    : 'bg-blue-50 text-blue-600 border-blue-200'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${source === 'amadeus_offers' ? 'bg-emerald-400' : 'bg-blue-400'}`} />
                  {source === 'amadeus_offers' ? 'Live Prices' : 'Amadeus Verified'}
                </span>
              </div>
              {source === 'amadeus_listings' && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex items-start gap-3">
                  <AlertCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-blue-800">Showing real hotels from Amadeus</p>
                    <p className="text-[11px] text-blue-600 mt-0.5">
                      Live pricing for this city requires production API access. Contact us for the best rates on any hotel listed below.
                    </p>
                  </div>
                </div>
              )}
              {results.map((hotel, i) => (
                <HotelCard key={hotel.hotelId || hotel.offerId || i} hotel={hotel} onSelect={setSelectedHotel} index={i} />
              ))}
            </div>
          </div>
        )}

        {/* No results */}
        {searchParams && !isLoading && results.length === 0 && !error && (
          <div className="bg-white rounded-2xl p-16 text-center shadow-sm border border-gray-100">
            <div className="w-20 h-20 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-5">
              <Hotel size={32} className="text-gray-300" />
            </div>
            <p className="text-gray-800 font-bold text-lg">No hotels available</p>
            <p className="text-gray-400 text-sm mt-2 max-w-md mx-auto">
              Try searching a different city, adjust your dates, or use the city code (e.g. "DEL" for Delhi, "LON" for London)
            </p>
            <button onClick={() => setSearchParams(null)}
              className="mt-5 text-sm text-[#243185] font-bold hover:underline">← Back to explore</button>
          </div>
        )}

        {/* ════ LANDING ════ */}
        {!searchParams && !isLoading && (
          <div className="space-y-12">
            {/* Quick Search Destinations */}
            <section>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-black text-gray-900">Quick Search</h2>
                <span className="text-xs text-gray-400">Pick a city to explore hotels</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {POPULAR_DESTINATIONS.map((dest, i) => (
                  <motion.button key={dest.code} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    onClick={() => {
                      const t = new Date(); const ci = new Date(t); ci.setDate(ci.getDate() + 3);
                      const co = new Date(ci); co.setDate(co.getDate() + 2);
                      handleSearch({ city: `${dest.name} (${dest.code})`, checkIn: ci.toISOString().split('T')[0], checkOut: co.toISOString().split('T')[0], adults: 2, rooms: 1 });
                    }}
                    className="group text-left relative rounded-2xl overflow-hidden h-44 shadow-sm hover:shadow-xl transition-all duration-300">
                    <img src={dest.img} alt={dest.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <p className="text-white font-black text-base leading-tight">{dest.name}</p>
                      <p className="text-white/60 text-[11px] mt-0.5">{dest.country}</p>
                    </div>
                  </motion.button>
                ))}
              </div>
            </section>

            {/* REAL Popular Hotels from Amadeus */}
            {popularLoading && (
              <div className="flex justify-center py-10">
                <div className="flex items-center gap-3 text-gray-400">
                  <Loader2 size={20} className="animate-spin" />
                  <span className="text-sm font-medium">Loading real hotels from Amadeus...</span>
                </div>
              </div>
            )}

            {(popularData || []).length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-black text-gray-900">Popular Hotels</h2>
                    <p className="text-sm text-gray-400 mt-1">Real properties from Amadeus global distribution</p>
                  </div>
                  <span className="text-[10px] bg-emerald-50 text-emerald-600 px-3 py-1.5 rounded-full font-bold border border-emerald-200 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live Data
                  </span>
                </div>

                <div className="space-y-8">
                  {(popularData || []).map((cityData, ci) => (
                    <motion.div key={cityData.city.code} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: ci * 0.08 }}>
                      {/* City Header */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-[#243185]/10 flex items-center justify-center">
                          <MapPin size={14} className="text-[#243185]" />
                        </div>
                        <div>
                          <h3 className="text-base font-bold text-gray-900">{cityData.city.name}, {cityData.city.country}</h3>
                          <p className="text-[11px] text-gray-400">{cityData.hotels.length} properties nearby</p>
                        </div>
                        <button onClick={() => {
                          const t = new Date(); const ci2 = new Date(t); ci2.setDate(ci2.getDate() + 3);
                          const co = new Date(ci2); co.setDate(co.getDate() + 2);
                          handleSearch({ city: `${cityData.city.name} (${cityData.city.code})`, checkIn: ci2.toISOString().split('T')[0], checkOut: co.toISOString().split('T')[0], adults: 2, rooms: 1 });
                        }} className="ml-auto text-xs text-[#243185] font-bold hover:underline flex items-center gap-1">
                          Search all <ArrowRight size={12} />
                        </button>
                      </div>

                      {/* Hotel Cards Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {cityData.hotels.map((hotel, hi) => (
                          <div key={hotel.hotelId}
                            onClick={() => setSelectedHotel(hotel)}
                            className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-[#243185]/20 hover:shadow-xl transition-all cursor-pointer">
                            <div className="relative h-32 overflow-hidden">
                              <HotelImage hotel={hotel} index={hi} size="sm" />
                            </div>
                            <div className="p-3">
                              <h4 className="text-sm font-bold text-gray-900 truncate group-hover:text-[#243185] transition-colors">
                                {hotel.name}
                              </h4>
                              <p className="text-[11px] text-gray-500 truncate flex items-center gap-1 mt-1">
                                <MapPin size={10} className="text-[#243185] flex-shrink-0" />
                                {hotel.address?.lines?.[0] || hotel.address?.cityName || cityData.city.name}
                              </p>
                              {hotel.distance && (
                                <p className="text-[10px] text-[#243185] font-medium mt-1">
                                  {hotel.distance.value} {hotel.distance.unit} from center
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </section>
            )}

            {/* Features */}
            <section className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-gray-100">
              <h2 className="text-2xl font-black text-gray-900 mb-8 text-center">Why Book with Nikaba Travels?</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {[
                  { icon: Globe, title: 'Global Coverage', desc: 'Hotels in 190+ countries via Amadeus', color: 'bg-blue-50 text-blue-600' },
                  { icon: Shield, title: 'Best Price Match', desc: 'Real-time rates, no hidden fees', color: 'bg-emerald-50 text-emerald-600' },
                  { icon: CreditCard, title: 'Secure Payments', desc: 'Razorpay + Stripe integrated', color: 'bg-purple-50 text-purple-600' },
                  { icon: ConciergeBell, title: '24/7 Concierge', desc: 'Expert travel support always', color: 'bg-amber-50 text-amber-600' },
                ].map((item, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.07 }}
                    className="text-center">
                    <div className={`w-14 h-14 rounded-2xl ${item.color} flex items-center justify-center mx-auto mb-4`}>
                      <item.icon size={24} />
                    </div>
                    <p className="text-sm font-bold text-gray-900">{item.title}</p>
                    <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{item.desc}</p>
                  </motion.div>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedHotel && (
          <HotelDetailModal hotel={selectedHotel} onClose={() => setSelectedHotel(null)} onBook={handleBook} />
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default Hotels;
